import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase'; 
import { collection, onSnapshot, updateDoc, doc, query, orderBy, getDocs, where, arrayUnion, serverTimestamp } from 'firebase/firestore';
import './ExpressOrders.css'; 

const ExpressOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  
  // Active vs Cancelled Tab State
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'cancelled'

  // Dynamic Agents List from Firebase
  const [agentsList, setAgentsList] = useState([]); 
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkAgent, setBulkAgent] = useState('');
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  // 🎯 Scroll Synchronization Refs (Top Scrollbar & Table Wrapper)
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  // Sync scroll positions between top bar and table wrapper
  const handleTopScroll = () => {
    if (bottomScrollRef.current && topScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  // Recalculate inner scrollable width for top scrollbar
  useEffect(() => {
    if (bottomScrollRef.current) {
      setScrollWidth(bottomScrollRef.current.scrollWidth);
    }
  }, [filteredOrders, activeTab]);

  // 1. Firebase Firestore 'agents' collection fetch
  useEffect(() => {
    const qAgents = query(collection(db, 'agents'), orderBy('companyName', 'asc'));
    const unsubscribeAgents = onSnapshot(qAgents, (snapshot) => {
      const fetchedAgents = snapshot.docs
        .map(doc => {
          const data = doc.data();
          const company = (data.companyName || '').trim();
          const location = (data.location || '').trim();
          const agentName = (data.agentName || '').trim();

          if (agentName || company) {
            return {
              id: doc.id,
              companyName: company || 'N/A',
              location: location || 'N/A',
              agentName: agentName || company,
            };
          }
          return null;
        })
        .filter(item => item !== null); 
      
      setAgentsList(fetchedAgents);
    }, (error) => {
      console.error("Error fetching agents list: ", error);
    });

    return () => unsubscribeAgents();
  }, []);

  // 2. Express Orders fetch
  useEffect(() => {
    const q = query(collection(db, 'expressOrder'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map((doc, index) => {
        const data = doc.data();
        const autoOrderId = data.displayOrderId || `MW-EXP-${1000 + snapshot.docs.length - index}`;
        return {
          id: doc.id,
          autoGeneratedId: autoOrderId,
          ...data,
          assignedAgent: data.assignedAgent || 'Unassigned', 
          serviceQuality: data.serviceQuality || 'Standard',
        };
      });
      setOrders(ordersData);
    }, (error) => {
      console.error("Error fetching express orders: ", error);
    });
    return () => unsubscribe();
  }, []);

  // Filter Orders based on Search, Date & Active/Cancelled Tab
  useEffect(() => {
    let result = orders;

    if (activeTab === 'cancelled') {
      result = result.filter(order => order.orderStatus === 'Cancelled' || order.orderStatus === 'Cancel Order');
    } else {
      result = result.filter(order => order.orderStatus !== 'Cancelled' && order.orderStatus !== 'Cancel Order');
    }

    if (searchTerm) {
      result = result.filter(order => 
        (order.userName && order.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.userPhone && order.userPhone.includes(searchTerm))
      );
    }
    if (filterDate) {
      result = result.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = order.createdAt.toDate().toISOString().split('T')[0];
        return orderDate === filterDate;
      });
    }
    setFilteredOrders(result);
    setSelectedOrderIds([]);
  }, [searchTerm, filterDate, orders, activeTab]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredOrders.map(order => order.id);
      setSelectedOrderIds(allIds);
    } else {
      setSelectedOrderIds([]);
    }
  };

  const handleSelectOrder = (id) => {
    if (selectedOrderIds.includes(id)) {
      setSelectedOrderIds(selectedOrderIds.filter(item => item !== id));
    } else {
      setSelectedOrderIds([...selectedOrderIds, id]);
    }
  };

  const updateCustomerOrderTracking = async (displayOrderId, status, description) => {
    try {
      const q = query(collection(db, 'orderTracking'), where('displayOrderId', '==', displayOrderId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const trackingDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'orderTracking', trackingDoc.id), {
          currentStatus: status,
          updatedAt: serverTimestamp(),
          statusLogs: arrayUnion({
            status: status,
            timestamp: new Date(),
            description: description
          })
        });
      }
    } catch (err) {
      console.error("Error updating orderTracking: ", err);
    }
  };

  const handleAssignSingleAgent = async (order, agentName) => {
    if (!agentName) return;

    if (order.orderStatus === 'Accepted' || order.orderStatus === 'Picked Up' || order.orderStatus === 'In-Transit' || order.orderStatus === 'Delivered') {
      return alert("This order has already been accepted/processed by the agent and cannot be reassigned!");
    }

    try {
      const newStatus = agentName === 'Unassigned' ? 'Pending' : 'Assigned';

      await updateDoc(doc(db, 'expressOrder', order.id), {
        assignedAgent: agentName,
        orderStatus: newStatus
      });

      if (agentName !== 'Unassigned') {
        await updateCustomerOrderTracking(
          order.autoGeneratedId, 
          'Agent assigned', 
          `Order assigned to delivery agent ${agentName}.`
        );
      }

      alert(`Order ${order.autoGeneratedId} assigned to ${agentName}!`);
    } catch (error) {
      alert("Error assigning agent: " + error.message);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAgent) return alert("Please select an agent first!");
    if (selectedOrderIds.length === 0) return alert("No orders selected!");

    try {
      const promises = selectedOrderIds.map(async (id) => {
        const targetOrder = orders.find(o => o.id === id);

        if (targetOrder && (targetOrder.orderStatus === 'Accepted' || targetOrder.orderStatus === 'Picked Up' || targetOrder.orderStatus === 'In-Transit' || targetOrder.orderStatus === 'Delivered')) {
          return; 
        }
        
        await updateDoc(doc(db, 'expressOrder', id), { 
          assignedAgent: bulkAgent,
          orderStatus: 'Assigned'
        });

        if (targetOrder && targetOrder.autoGeneratedId) {
          await updateCustomerOrderTracking(
            targetOrder.autoGeneratedId, 
            'Agent assigned', 
            `Order assigned to delivery agent ${bulkAgent}.`
          );
        }
      });

      await Promise.all(promises);
      alert(`Agent successfully assigned to selected eligible orders!`);
      setSelectedOrderIds([]);
      setBulkAgent('');
    } catch (error) {
      alert("Error assigning agent: " + error.message);
    }
  };

  const handleStatusChange = async (order, newStatus) => {
    try {
      const statusToSave = newStatus === 'Cancel Order' ? 'Cancelled' : newStatus;

      await updateDoc(doc(db, 'expressOrder', order.id), { orderStatus: statusToSave });

      if (statusToSave === 'Cancelled') {
        await updateCustomerOrderTracking(
          order.autoGeneratedId, 
          'Cancelled', 
          'Order was cancelled by store admin.'
        );
        alert(`Order ${order.autoGeneratedId} moved to Cancelled Orders.`);
      }
    } catch (error) {
      console.error("Error updating status: ", error);
    }
  };

  const handleRestoreOrder = async (order) => {
    if (!window.confirm(`Are you sure you want to restore order ${order.autoGeneratedId}?`)) return;

    try {
      const restoredStatus = order.assignedAgent && order.assignedAgent !== 'Unassigned' ? 'Assigned' : 'Pending';

      await updateDoc(doc(db, 'expressOrder', order.id), { orderStatus: restoredStatus });

      await updateCustomerOrderTracking(
        order.autoGeneratedId, 
        restoredStatus, 
        'Order has been restored by admin.'
      );

      alert(`Order ${order.autoGeneratedId} restored successfully!`);
    } catch (error) {
      alert("Error restoring order: " + error.message);
    }
  };

  const activeCount = orders.filter(o => o.orderStatus !== 'Cancelled' && o.orderStatus !== 'Cancel Order').length;
  const cancelledCount = orders.filter(o => o.orderStatus === 'Cancelled' || o.orderStatus === 'Cancel Order').length;

  // Helper function to render Payment Status Badge styling
  const getPaymentStatusBadge = (status) => {
    const pStatus = (status || 'Pending Pricing').trim();
    let bgColor = '#fef3c7';
    let textColor = '#92400e';

    if (pStatus.toLowerCase() === 'paid') {
      bgColor = '#dcfce7';
      textColor = '#166534';
    } else if (pStatus.toLowerCase() === 'unpaid' || pStatus.toLowerCase() === 'failed') {
      bgColor = '#fef2f2';
      textColor = '#dc2626';
    }

    return (
      <span style={{
        backgroundColor: bgColor,
        color: textColor,
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 'bold',
        display: 'inline-block',
        whiteSpace: 'nowrap'
      }}>
        {pStatus}
      </span>
    );
  };

  return (
    <div className="express-container">
      <div className="express-card">
        <div className="express-card-header">
          <h2 className="express-title">Laundry Orders Management</h2>
          
          <div className="tabs-wrapper">
            <button 
              className={`tab-btn ${activeTab === 'active' ? 'active-tab' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              📦 Active Orders <span className="tab-badge">{activeCount}</span>
            </button>
            <button 
              className={`tab-btn ${activeTab === 'cancelled' ? 'cancelled-tab' : ''}`}
              onClick={() => setActiveTab('cancelled')}
            >
              🚫 Cancelled Orders <span className="tab-badge cancel-badge">{cancelledCount}</span>
            </button>
          </div>
        </div>

        <div className="filter-bar">
          <div className="filter-inputs">
            <input 
              type="text" 
              placeholder="Search by Customer or Phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <input 
              type="date" 
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>

          {activeTab === 'active' && (
            <div className="bulk-actions">
              <select 
                value={bulkAgent} 
                onChange={(e) => setBulkAgent(e.target.value)}
                className="bulk-agent-select"
              >
                <option value="">-- Select Agent --</option>
                {agentsList.map((agent) => (
                  <option key={agent.id} value={agent.agentName}>
                    🏢 {agent.companyName} ({agent.agentName}) - 📍 {agent.location}
                  </option>
                ))}
              </select>
              <button onClick={handleBulkAssign} className="btn-assign">
                Assign Selected ({selectedOrderIds.length})
              </button>
            </div>
          )}
        </div>

        {/* 🎯 TABLE SECTION WITH DEDICATED TOP SCROLLBAR */}
        <div className="table-outer-container">
          {/* Top Horizontal Scrollbar */}
          <div 
            className="top-scrollbar-container" 
            ref={topScrollRef} 
            onScroll={handleTopScroll}
          >
            <div className="top-scrollbar-dummy" style={{ width: `${scrollWidth}px` }} />
          </div>

          {/* Actual Scrollable Table Wrapper */}
          <div 
            className="table-wrapper" 
            ref={bottomScrollRef} 
            onScroll={handleBottomScroll}
          >
            <table className="orders-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAll}
                      checked={selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0}
                    />
                  </th>
                  <th style={{ width: '380px' }}>Assigned Agent</th>
                  <th style={{ width: '130px' }}>Order ID</th>
                  <th style={{ width: '130px' }}>Payment Status</th>
                  <th style={{ width: '110px' }}>Order Type</th>
                  <th>Customer Details</th>
                  <th style={{ width: '110px' }}>Date</th>
                  <th style={{ width: '95px' }}>Status</th>
                  <th style={{ textAlign: 'center', width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      {activeTab === 'active' ? "No active express orders found." : "No cancelled express orders found."}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => {
                    const isAcceptedByAgent = order.orderStatus === 'Accepted' || 
                                              order.orderStatus === 'Picked Up' || 
                                              order.orderStatus === 'In-Transit' || 
                                              order.orderStatus === 'Delivered';

                    const paymentStatusValue = order.payment?.status || order.paymentStatus || "Pending Pricing";

                    return (
                      <tr key={order.id} className={activeTab === 'cancelled' ? 'row-cancelled' : ''}>
                        <td>
                          <input 
                            type="checkbox" 
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                            disabled={isAcceptedByAgent || activeTab === 'cancelled'}
                          />
                        </td>
                        <td>
                          <select
                            value={order.assignedAgent}
                            onChange={(e) => handleAssignSingleAgent(order, e.target.value)}
                            disabled={isAcceptedByAgent || activeTab === 'cancelled'}
                            className={`table-agent-select ${isAcceptedByAgent || activeTab === 'cancelled' ? 'disabled-select' : ''}`}
                            style={{
                              backgroundColor: (isAcceptedByAgent || activeTab === 'cancelled')
                                ? '#e2e8f0' 
                                : (order.assignedAgent === 'Unassigned' ? '#fef2f2' : '#f0fdf4'),
                              color: (isAcceptedByAgent || activeTab === 'cancelled')
                                ? '#475569' 
                                : (order.assignedAgent === 'Unassigned' ? '#dc2626' : '#16a34a'),
                            }}
                            title={isAcceptedByAgent ? "Task accepted by agent - cannot be reassigned" : order.assignedAgent}
                          >
                            <option value="Unassigned">-- Unassigned --</option>
                            {agentsList.map((agent) => (
                              <option key={agent.id} value={agent.agentName}>
                                🏢 {agent.companyName} - 📍 {agent.location}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="order-id">{order.autoGeneratedId}</td>

                        <td>
                          {getPaymentStatusBadge(paymentStatusValue)}
                        </td>
                        
                        <td>
                          <span className={`order-type-chip ${order.serviceQuality?.toLowerCase()}`}>
                             {order.serviceQuality || "Standard"}
                          </span>
                        </td>

                        <td>
                          <div className="customer-info-cell">
                            <span className="customer-name">{order.userName || order.customerName || "N/A"}</span>
                            <span className="customer-phone"> {order.userPhone || order.customerPhone || "N/A"}</span>
                          </div>
                        </td>

                        <td>
                          {order.createdAt ? order.createdAt.toDate().toLocaleDateString('en-GB') : 'Pending'}
                        </td>

                        <td>
                          {activeTab === 'cancelled' ? (
                            <span className="status-cancelled-badge">Cancelled</span>
                          ) : (
                            <select 
                              value={order.orderStatus || 'Pending'}
                              onChange={(e) => handleStatusChange(order, e.target.value)}
                              className="status-select"
                            >
                              <option value="Pending">Pending</option>
                              <option value="Assigned">Assigned</option>
                              <option value="Accepted">Accepted</option>
                              <option value="Picked Up">Picked Up</option>
                              <option value="In-Transit">In-Transit</option>
                              <option value="Delivered">Delivered</option>
                              <option value="Cancel Order" style={{ color: '#dc2626', fontWeight: 'bold' }}>❌ Cancel Order</option>
                            </select>
                          )}
                        </td>

                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button onClick={() => setSelectedOrderDetails(order)} className="btn-view">
                              👁️ View
                            </button>
                            
                            {activeTab === 'cancelled' && (
                              <button onClick={() => handleRestoreOrder(order)} className="btn-restore" title="Restore Order">
                                🔄 Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* BEAUTIFIED DETAIL VIEW MODAL */}
      {selectedOrderDetails && (
        <div className="modal-overlay" onClick={() => setSelectedOrderDetails(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Order Details ({selectedOrderDetails.autoGeneratedId})</h2>
                <div className="header-subtitle">
                  <span className="order-badge-id">
                    {selectedOrderDetails.displayOrderId || selectedOrderDetails.autoGeneratedId}
                  </span>
                  <span className={`status-tag ${selectedOrderDetails.orderStatus?.toLowerCase().replace(/\s+/g, '-') || 'pending'}`}>
                    {selectedOrderDetails.orderStatus || 'Pending'}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedOrderDetails(null)} className="modal-close-btn">&times;</button>
            </div>

            <div className="modal-body">
              <div className="info-grid">
                <div className="info-box">
                  <div className="info-label">Order ID & Type:</div>
                  <div className="info-value">
                    {selectedOrderDetails.displayOrderId || selectedOrderDetails.autoGeneratedId || "N/A"} 
                    <span style={{ fontSize: '12px', marginLeft: '8px', color: '#ea580c', fontWeight: 'bold' }}>
                      ({selectedOrderDetails.serviceQuality || "Standard"})
                    </span>
                  </div>
                </div>
                <div className="info-box">
                  <div className="info-label">Customer Identity:</div>
                  <div className="info-value">
                    {selectedOrderDetails.customerName || selectedOrderDetails.userName || "N/A"} ({selectedOrderDetails.customerPhone || selectedOrderDetails.userPhone || "N/A"})
                  </div>
                </div>
                <div className="info-box">
                  <div className="info-label">Assigned Agent:</div>
                  <div className="info-value agent-highlight">{selectedOrderDetails.assignedAgent || 'Unassigned'}</div>
                </div>
                <div className="info-box">
                  <div className="info-label">Booked Date:</div>
                  <div className="info-value">
                    {selectedOrderDetails.createdAt ? selectedOrderDetails.createdAt.toDate().toLocaleString() : 'Pending'}
                  </div>
                </div>

                <div className="requirements-box">
                  <strong>⚡ Express Laundry Requirements:</strong>
                  <p style={{ margin: '6px 0 12px 0', fontStyle: 'italic' }}>
                    {selectedOrderDetails.expressRequirements || "No description provided."}
                  </p>

                  {selectedOrderDetails.cart && selectedOrderDetails.cart.length > 0 && (
                    <div className="modal-cart-inner" style={{ marginTop: '12px', borderTop: '1px dashed #fdba74', paddingTop: '12px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#9a3412', fontSize: '13px' }}>
                        🛒 Updated Order Cart Items:
                      </strong>
                      <div className="modal-table-wrapper">
                        <table className="modal-table" style={{ fontSize: '12px' }}>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Service</th>
                              <th>Item Name</th>
                              <th>Type</th>
                              <th>Qty / Weight</th>
                              <th>Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedOrderDetails.cart.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td>{idx + 1}</td>
                                <td><strong>{item.serviceName || '-'}</strong></td>
                                <td>{item.name || '-'}</td>
                                <td><span className="type-chip">{item.pricingType || '-'}</span></td>
                                <td>
                                  {item.pricingType === 'kg' || item.pricingType === 'weight'
                                    ? `${item.weight || 0} kg`
                                    : `${item.quantity || 1} Pcs`}
                                </td>
                                <td className="rate-amount">
                                  AED {item.offerPrice || item.itemRate || '0.00'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="address-box">
                <h4>Pickup Address</h4>
                {selectedOrderDetails.address ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <p style={{ margin: 0 }}><strong>Villa/Bldg:</strong> {selectedOrderDetails.address.villa || "N/A"}</p>
                    <p style={{ margin: 0 }}><strong>Street:</strong> {selectedOrderDetails.address.street || "N/A"}</p>
                    <p style={{ margin: 0 }}><strong>Landmark:</strong> {selectedOrderDetails.address.landmark || "N/A"}</p>
                    <p style={{ margin: 0 }}><strong>City:</strong> {selectedOrderDetails.address.city || "N/A"}</p>
                    <p style={{ margin: '4px 0 0 0', gridColumn: 'span 2' }}><strong>Contact Phone:</strong> {selectedOrderDetails.address.phone || "N/A"}</p>
                  </div>
                ) : <p className="no-data-text">No address data found.</p>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="section-box">
                  <h4>📅 Scheduling</h4>
                  {selectedOrderDetails.scheduling?.pickup ? (
                    <div className="info-list">
                      <p style={{ margin: '0 0 4px 0' }}><strong>Pickup:</strong> {selectedOrderDetails.scheduling.pickup.date} | {selectedOrderDetails.scheduling.pickup.time}</p>
                      <p style={{ margin: 0 }}><strong>Dropoff:</strong> {selectedOrderDetails.scheduling.dropoff?.date} | {selectedOrderDetails.scheduling.dropoff?.time}</p>
                    </div>
                  ) : <p className="no-data-text">N/A</p>}
                </div>

                <div className="section-box">
                  <h4>👔 Preferences</h4>
                  {selectedOrderDetails.preferences ? (
                    <div className="info-list">
                      <p style={{ margin: '0 0 4px 0' }}><strong>Starch:</strong> {selectedOrderDetails.preferences.starchLevel || "None"}</p>
                      <p style={{ margin: '0 0 4px 0' }}><strong>Notes:</strong> {selectedOrderDetails.preferences.specialInstructions || "None"}</p>
                      <p style={{ margin: 0 }}><strong>Consent Given:</strong> {selectedOrderDetails.preferences.consentGiven ? "✅ Yes" : "❌ No"}</p>
                    </div>
                  ) : <p className="no-data-text">N/A</p>}
                </div>
              </div>

              <div className="payment-summary">
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>Payment Status</div>
                  <span className="payment-status">{selectedOrderDetails.payment?.status || "Pending Pricing"}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>Total Payable</div>
                  <div className="total-price">AED {selectedOrderDetails.payment?.totalPayable || "0.00"}</div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setSelectedOrderDetails(null)} className="btn-modal-close">Close Window</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpressOrders;